import React from "react";
import styled, {css, ThemeProvider} from "styled-components";

import {v4 as uuidv4} from "uuid";
import {useAppSelector} from "../../app/hooks";
import {scheduleState} from "../../app/reducers/schedule-slice";
import theme from "../../style/theme";
import {DeliveryGroup} from "../../types/schedule/delivery-group";
import {Schedule} from "../../types/schedule/schedule";
import {ScheduleStatus} from "../../types/schedule/schedule-status";
import {timeFormat} from "../../utils/date/date-format";
import RecompleteIcon from "../icons/\bRecompleteIcon";
import CheckCircleFillIcon from "../icons/CheckCircleFillIcon/CheckCircleFillIcon";
import ErrorTriangleFillIcon from "../icons/ErrorTriangleFillIcon/ErrorTriangleFillIcon";
import TimeLineIcon from "../icons/TimeLineIcon/TimeLineIcon";
import RoundedTextBadge from "../badge/RoundedTextBadge";

interface DeliveryGroupCardProps {
    children?: string;

    id?: string | number;

    label?: string;

    cardDeliveryGroupNumber?: number;

    deliveryGroup: DeliveryGroup;

    disabled?: boolean;

    style?: React.CSSProperties;

    value?: string | number;

    onChange?: (e: any) => void;

    onClick?: (e?: any) => void;

    schedule: Schedule;
    deliveryGroupIndex: number;

    isCurrentDeliveryGroup: boolean;
}

export const DeliveryGroupCard: React.FC<DeliveryGroupCardProps> = (
    {
        children,
        style,
        id,
        onChange,
        onClick,
        value,
        deliveryGroup,
        cardDeliveryGroupNumber,
        schedule,
        deliveryGroupIndex,
        isCurrentDeliveryGroup,
        ...rest
    }
) => {
    const {dialogSchedule, dialogDeliveryGroupIndex} =
        useAppSelector(scheduleState);

    const isExpectedDelay =
        Boolean(deliveryGroup.isExpectedDelay) && isCurrentDeliveryGroup;

    const isSelected =
        deliveryGroupIndex === dialogDeliveryGroupIndex &&
        schedule.id === dialogSchedule?.id;

    const getCurrentTimeIcon = (deliveryGroup: DeliveryGroup) => {
        const getStatusElements = (
            icon: JSX.Element,
            time: string,
            status: string,
            color: string
        ): JSX.Element => (
            <>
                {icon}
                <CurrentTime style={{color}}>{time}</CurrentTime>
                <CurrentStatus style={{color}}>{status}</CurrentStatus>
            </>
        );

        if (schedule.status === ScheduleStatus.Wait || deliveryGroup.isWait) {
            return getStatusElements(
                <ExtendedCheckCircleFillIcon fill={`${theme.palette.primitives.gray400}`}/>,
                "00:00",
                "진행전",
                "#979DA8"
            );
        }

        if (schedule.status === ScheduleStatus.Completed || deliveryGroup.isReported) {
            return getStatusElements(
                <ExtendedCheckCircleFillIcon fill={`${theme.palette.primitives.green400}`}/>,
                timeFormat(deliveryGroup.reportedAt),
                "완료",
                "#333333"
            );
        }

        if (deliveryGroup.isWork) {
            const color = deliveryGroup.isExpectedDelay ? "#F94D57" : "#333333";
            const icon = deliveryGroup.isExpectedDelay
                ? <ExtendedErrorTriangleFillIcon/>
                : <ExtendedTimeLineIcon fill={`${theme.palette.primitives.black}`}/>;
            return getStatusElements(
                icon,
                timeFormat(deliveryGroup.estimatedTimeOfArrival),
                "예상",
                color
            );
        }
    };

    return (
        <>
            <ThemeProvider theme={theme}>
                <DeliveryGroupCardContainer
                    {...rest}
                    style={style}
                    className={`${uuidv4()} ${
                        deliveryGroup.isExpectedDelay && !deliveryGroup.isReported
                            ? " expect-delay "
                            : " "
                    } ${deliveryGroup.isReported && "complete"} ${
                        isSelected && " selected "
                    }`}
                    deliveryGroup={deliveryGroup}
                    isSelected={isSelected}
                    expectDelay={isExpectedDelay}
                    isCurrentDeliveryGroup={
                        isCurrentDeliveryGroup && schedule.status === ScheduleStatus.Work
                    }
                    onClick={onClick}
                >
                    <DeliveryGroupCardContent>
                        <DeliveryGroupCardHeader>
                            <DeliveryGroupMainLabel>
                                <LineNumberButton
                                    isPickUp={deliveryGroup.isPickUp}>{cardDeliveryGroupNumber}</LineNumberButton>
                                <DeliveryGroupTypeBox>{deliveryGroup.isPickUp ? "상차" : "전달"}</DeliveryGroupTypeBox>
                                <DeliveryGroupIsDelay>
                                    {deliveryGroup.isDelay && "지연"}
                                </DeliveryGroupIsDelay>
                            </DeliveryGroupMainLabel>
                            {deliveryGroup.retryCount > 0 && (
                                <RetryLabel>
                                    <RecompleteIcon/>
                                    <RetryLabelText>재배송</RetryLabelText>
                                </RetryLabel>
                            )}
                        </DeliveryGroupCardHeader>

                        <DeliveryGroupInfoBox>
                            <MainAddress>{deliveryGroup.formattedAddress}</MainAddress>
                        </DeliveryGroupInfoBox>

                        <RequestTimeContainer>
                            <ExtendedTimeLineIcon
                                fill={`${theme.palette.primitives.gray400}`}
                            />
                            <RequestTime>
                                {timeFormat(deliveryGroup.startTimeLimited)}
                            </RequestTime>
                            <RequestStatus>요청</RequestStatus>
                        </RequestTimeContainer>

                        <CurrentTimeContainer>
                            <CurrentStatusRow>
                                {getCurrentTimeIcon(deliveryGroup)}
                            </CurrentStatusRow>
                            {schedule.hasMultipleClients && deliveryGroup.clientSequence && (
                                <RoundedTextBadgeRow>
                                    <RoundedTextBadge text={deliveryGroup.clientSequence}
                                                      clientName={deliveryGroup.deliveriesInGroup[0].client.name}/>
                                </RoundedTextBadgeRow>
                            )}
                        </CurrentTimeContainer>
                    </DeliveryGroupCardContent>
                </DeliveryGroupCardContainer>
            </ThemeProvider>
        </>
    );
};

const deliveryGroupStatusStyles: any = css`
  ${(props: any) => {
    return css`
      ${(props: any) =>
              props.deliveryGroup.isWait &&
              css`
                background: ${(props: any) =>
                        props.theme.palette.primitives.gray100};

                & svg path {
                  fill: ${(props: any) => props.theme.palette.primitives.gray400};
                }

                & .finished-status {
                  color: ${(props: any) => props.theme.palette.primitives.gray400};
                }

                & > div {
                  border-right: 1px solid ${(props: any) => props.theme.palette.primitives.gray200};
                }
              `}
    `;
  }};
  /* color secondary */
  ${(props: any) => {
    return css`
      ${(props: any) =>
              props.deliveryGroup.isStart &&
              css`
                color: ${(props: any) => props.theme.palette.primitives.black};
              `}
    `;
  }};

  /* color tertiary */
  ${(props: any) => {
    return css`
      ${(props: any) =>
              props.deliveryGroup.isReported &&
              css`
                & .finished-status,
                & .finished-time {
                  color: ${(props: any) => props.theme.palette.primitives.black};
                  font-weight: 700;
                }

                & .complete svg path {
                  fill: ${(props: any) => props.theme.palette.primitives.green400};
                }
              `}
    `;
  }};
`;

interface DeliveryGroupCardContainerProps {
    isSelected: boolean;
    expectDelay: boolean;
    isCurrentDeliveryGroup: boolean;
    deliveryGroup: DeliveryGroup;
}

const DeliveryGroupCardContainer = styled.div<DeliveryGroupCardContainerProps>`
  width: 169px;
  height: 133px;
  padding: 16px 0;
  box-sizing: border-box;
  border: 1px solid transparent;
  background: ${(props: any) => props.theme.palette.primitives.white};

  ${deliveryGroupStatusStyles};

  ${(props) =>
          props.isCurrentDeliveryGroup &&
          css`
            border: none;
            border-top: 5px solid ${props.deliveryGroup.isPickUp ? theme.palette.primitives.blue400 : theme.palette.primitives.yellow400};
            padding-top: 12px;
          `};

  &.expect-delay {
    border: 1px solid transparent;
    background: ${(props) => props.theme.palette.primitives.red100};
    border-top: 5px solid ${theme.palette.primitives.red600};
    padding-top: 12px;
  }

  &.expect-delay.complete {
    border: none;
    background: ${(props: any) => props.theme.palette.primitives.white};
  }

  &.expect-delay .finished-status {
    color: ${(props: any) => props.theme.palette.primitives.red500};
  }

  &.expect-delay .finished-time {
    color: ${(props: any) => props.theme.palette.primitives.red500};
  }

  ${(props) =>
          props.isSelected &&
          css`
            padding-top: 16px !important;
            box-sizing: border-box;
            border: 1px solid ${(props: any) => props.theme.palette.primitives.blue400};
            background: ${(props: any) => props.theme.palette.primitives.blue50};

            &.expect-delay {
              border: 1px solid ${(props: any) => props.theme.palette.primitives.blue400};
              background: ${(props: any) => props.theme.palette.primitives.blue50};
            }
          `}
  & .complete.finished-status,
  & .complete.finished-time {
    color: ${(props: any) => props.theme.palette.primitives.black};
    font-weight: 700;
  }

  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${(props: any) => props.theme.palette.primitives.gray100};
  }

  &.expect-delay:not(.complete):hover {
    background: ${(props: any) => props.theme.palette.primitives.red100};
    opacity: 0.85;
  }

  &.complete:hover {
    background: ${(props: any) => props.theme.palette.primitives.white};
  }

  ${(props) =>
          props.isSelected &&
          css`
            &:hover {
              background: ${(props: any) => props.theme.palette.primitives.blue50};
            }
          `}
`;

const DeliveryGroupCardContent = styled.div`
  border-right: 1px solid ${(props: any) => props.theme.palette.primitives.gray100};
  padding: 0 16px;
`;

const DeliveryGroupCardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 3px;
  width: 100%;
  justify-content: space-between;
`;

const DeliveryGroupInfoBox = styled.div`
  width: 137px;
  height: 38px;
  margin-bottom: 8px;
`;

const LineNumberButton = styled.button<{
    isPickUp: Boolean,
}>`
  border: none;
  padding: 0;
  width: 16px;
  height: 16px;
  background-color: ${(props => props.isPickUp ? theme.palette.primitives.blue400 : theme.palette.primitives.yellow400)};
  font-weight: 900;
  font-size: 11px;
  color: ${(props: any) => props.theme.palette.primitives.white};
  margin-right: 4px;
`;

const MainAddress = styled.p`
  width: 137px;
  margin: 0;
  font-weight: 600;
  font-size: 15px;
  line-height: 19px;
  color: ${(props: any) => props.theme.palette.primitives.gray700};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`;

const DeliveryGroupTypeBox = styled.p`
  height: 16px;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props: any) => props.theme.palette.primitives.gray500};
  margin-right: 2px;
`;

const DeliveryGroupIsDelay = styled.p`
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  color: #f94d57;
  margin-right: 2px;
`;

const RequestTimeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const ExtendedTimeLineIcon = styled(TimeLineIcon)`
  width: 17px;
  height: 17px;

  margin-right: 2px;
`;

const ExtendedCheckCircleFillIcon = styled(CheckCircleFillIcon)`
  width: 17px;
  height: 17px;

  margin-right: 2px;
`;

const ExtendedErrorTriangleFillIcon = styled(ErrorTriangleFillIcon)`
  width: 17px;
  height: 17px;

  margin-right: 2px;
`;

const RequestTime = styled.p`
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
  color: ${(props: any) => props.theme.palette.primitives.gray400};
  margin: 0 2px 0 0;
`;

const RequestStatus = styled.span`
  margin: 0;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
  color: ${(props: any) => props.theme.palette.primitives.gray400};
`;

const CurrentTimeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CurrentStatusRow = styled.div`
  display: flex;
  align-items: center;
`;

const RoundedTextBadgeRow = styled.div`
  display: flex;
  align-items: center;
`;

const CurrentTime = styled.p`
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  color: ${(props: any) => props.theme.palette.primitives.gray400};
  margin: 0 2px 0 0;
`;

const CurrentStatus = styled.span`
  margin: 0;
  font-weight: 700;
  font-size: 13px;
  line-height: 16px;
  color: ${(props: any) => props.theme.palette.primitives.gray400};
`;

const RetryLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  justify-content: center;
`;

const RetryLabelText = styled.div`
  font-size: 12px;
  color: ${theme.palette.primitives.gray400};
  font-weight: 500;
  line-height: normal;
`;

const DeliveryGroupMainLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
